import React, { useMemo, useState } from 'react';
import type { PlaceLocation } from '@goodfynd/context';
import { GeoInput, useFormContext } from '@goodfynd/forms';
import { FlexView } from '@goodfynd/ui';
import { Text } from '@goodfynd/ui';
import useVendorFrequentLocations from '@hooks/useVendorFrequentLocations';
import Autocomplete from '@mui/material/Autocomplete';
import MenuItem from '@mui/material/MenuItem';
import TextField from '@mui/material/TextField';

import type { ScheduleWizardFields } from './types';

export function StepOperatingLocation() {
  const { control, watch } = useFormContext<ScheduleWizardFields>();
  const [savedLocation, setSavedLocation] = useState<PlaceLocation>();
  const vendorId = watch('vendorId');
  const { frequentLocations } = useVendorFrequentLocations({ vendorId });
  const savedLocationOptions = useMemo(
    () =>
      frequentLocations
        ?.filter((option) => option.coordinates)
        .map((option) => ({
          label: option.name || option.address,
          value: option.coordinates,
        })) || [],
    [frequentLocations]
  );

  return (
    <FlexView direction="column" gap={16}>
      <Text as="body2">Where will you be vending?</Text>

      {frequentLocations && (
        <>
          <Autocomplete
            disablePortal
            fullWidth
            options={savedLocationOptions}
            onChange={(_e, selectedOption) => {
              const location = frequentLocations?.find(
                (option) => option.coordinates === selectedOption?.value
              );
              setSavedLocation(location);
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Saved locations"
                placeholder="Search for a location name..."
              />
            )}
            renderOption={(props, option) => {
              return (
                <MenuItem {...props} key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              );
            }}
            value={
              savedLocationOptions.find(
                (option) =>
                  option.value === savedLocation?.coordinates &&
                  watch('location')?.coordinates === savedLocation?.coordinates
              ) || null
            }
          />
        </>
      )}

      <GeoInput<ScheduleWizardFields>
        control={control}
        css={{ marginTop: 32 }}
        enableLocationName
        id="location"
        label="Set new location"
        lookup
        name="location"
        showAddressFields
        showMap
        updatedLocation={savedLocation}
      />
    </FlexView>
  );
}

export function StepOperatingLocationSummary() {
  const { getValues } = useFormContext<ScheduleWizardFields>();
  const { location } = getValues();

  return (
    <div>
      <Text id="name">{location?.name}</Text>
      {location?.name !== location?.address && (
        <Text id="address">{location?.address}</Text>
      )}
    </div>
  );
}
